import React, { useState, useEffect } from "react";
import "./RosterPage.css";
import apiClient from "../utils/apiClient";
import SessionHandler from "../components/SessionHandler";

const RosterPage = () => {
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]); // 👈 start EMPTY
  const [selectedDept, setSelectedDept] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toISOString().slice(0, 7)
  );
  const [wfoRoster, setWfoRoster] = useState({});
  const [timeRoster, setTimeRoster] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const weekDayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const shiftOptions = [
    "06:00AM to 03:00PM",
    "07:00AM to 04:00PM",
    "08:00AM to 05:00PM",
    "09:00AM to 05:00PM",
    "09:00AM to 06:00PM",
    "09:30AM to 05:30PM",
    "10:00AM to 07:00PM",
    "10:30AM to 06:30PM",
    "11:00AM to 08:00PM",
    "11:30AM to 07:30PM",
    "12:00PM to 09:00PM",
    "01:00PM to 10:00PM",
    "02:00PM to 11:00PM",
    "03:00PM to 12:00AM",
    "04:00PM to 01:00AM",
    "05:00PM to 02:00AM",
    "06:00PM to 03:00AM",
    "07:00PM to 04:00AM",
    "08:00PM to 05:00AM",
    "09:00PM to 06:00AM",
    "10:00PM to 07:00AM",
    "11:00PM to 08:00AM",
    "Weekoff",
    "US Holiday",
  ];

  /* ================= FETCH EMPLOYEES ================= */
  useEffect(() => {
    const fetchEmployees = async () => {
      setLoading(true);
      try {
        const data = await apiClient("/auth/allemployees/");
        if (!data) return;

        const sorted = data.sort((a, b) =>
          a.department?.localeCompare(b.department)
        );

        setEmployees(sorted);

        const uniqueDepartments = [
          ...new Set(sorted.map((emp) => emp.department).filter(Boolean)),
        ];
        setDepartments(uniqueDepartments);
      } catch (err) {
        console.error("Failed to fetch employees:", err);
        setError("Failed to fetch employees");
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, []);

  /* ================= FETCH ROSTER ================= */
  useEffect(() => {
    if (!selectedDept || !selectedMonth) return;

    const fetchRoster = async () => {
      setLoading(true);
      try {
        const data = await apiClient(
          `/roster/view-roster/?department=${selectedDept}&month=${selectedMonth}`
        );

        const newWfo = {};
        const newTime = {};

        data?.forEach((emp) => {
          newWfo[emp.id] = {};
          newTime[emp.id] = {};

          if (emp.roster) {
            Object.keys(emp.roster).forEach((day) => {
              newWfo[emp.id][day] = emp.roster[day].wfo;
              newTime[emp.id][day] = emp.roster[day].time;
            });
          }
        });

        setWfoRoster(newWfo);
        setTimeRoster(newTime);
      } catch (err) {
        console.error("Failed to fetch roster:", err);
        setError("Failed to fetch roster");
      } finally {
        setLoading(false);
      }
    };

    fetchRoster();
  }, [selectedDept, selectedMonth]);

  /* ================= HELPERS ================= */
  const getDaysInMonth = () => {
    const [year, month] = selectedMonth.split("-");
    return new Date(year, month, 0).getDate();
  };

  const getWeekdayName = (year, month, day) =>
    weekDayLabels[new Date(year, month - 1, day).getDay()];

  const handleWFOChange = (empId, day, value) => {
    setWfoRoster((prev) => ({
      ...prev,
      [empId]: { ...prev[empId], [day]: value },
    }));
  };

  const handleTimeChange = (empId, day, value) => {
    setTimeRoster((prev) => ({
      ...prev,
      [empId]: { ...prev[empId], [day]: value },
    }));
  };

  /* ================= DEPARTMENT CHANGE ================= */
  const handleDepartmentChange = (e) => {
    const dept = e.target.value;
    setSelectedDept(dept);
    setMessage("");
    setWfoRoster({});
    setTimeRoster({});

    if (!dept) {
      setFilteredEmployees([]);
      return;
    }

    const filtered = employees.filter(
      (emp) => emp.department === dept
    );
    setFilteredEmployees(filtered);
  };

  /* ================= SAVE ================= */
  const handleSubmit = async () => {
    setSaving(true);
    setMessage("");

    const days = getDaysInMonth();
    const [year, month] = selectedMonth.split("-");
    const roster = {};

    filteredEmployees.forEach((emp) => {
      roster[emp.id] = {};
      for (let d = 1; d <= days; d++) {
        const dayName = getWeekdayName(year, month, d);
        roster[emp.id][d] = {
          wfo: wfoRoster[emp.id]?.[dayName] || false,
          time: timeRoster[emp.id]?.[dayName] || "",
        };
      }
    });

    try {
      await apiClient("/roster/save-roster/", {
        method: "POST",
        body: JSON.stringify({
          department: selectedDept,
          month: selectedMonth,
          roster,
        }),
      });
      setMessage("✅ Roster saved successfully!");
    } catch {
      setMessage("❌ Failed to save roster");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="roster-page-container">
      <h2 className="roster-page-title">
        Employee Roster (WFO + Shift Time)
      </h2>

      <div className="roster-controls">
        <label>Department:</label>
        <select value={selectedDept} onChange={handleDepartmentChange}>
          <option value="">-- Select Department --</option>
          {departments.map((dept) => (
            <option key={dept} value={dept}>
              {dept}
            </option>
          ))}
        </select>

        <label>Month:</label>
        <input
          type="month"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          disabled={!selectedDept}
        />
      </div>

     {selectedDept && filteredEmployees.length > 0 && (
  <div className="roster-table-wrapper">
    <table className="roster-table">
      <thead>
        <tr>
          <th className="sticky-col">Employee</th>
          {weekDayLabels.map((day) => (
            <th key={day}>{day}</th>
          ))}
        </tr>
      </thead>

      <tbody>
        {filteredEmployees.map((emp) => (
          <tr key={emp.id}>
            <td className="sticky-col emp-name">{emp.name}</td>

            {weekDayLabels.map((day) => (
              <td key={day}>
                <div className="cell-inner">
                  <label className="wfo-check">
                    <input
                      type="checkbox"
                      checked={wfoRoster[emp.id]?.[day] || false}
                      onChange={(e) =>
                        handleWFOChange(emp.id, day, e.target.checked)
                      }
                    />
                    <span>WFO</span>
                  </label>

                  <select
                    value={timeRoster[emp.id]?.[day] || ""}
                    onChange={(e) =>
                      handleTimeChange(emp.id, day, e.target.value)
                    }
                  >
                    <option value="">Shift</option>
                    {shiftOptions.map((slot, idx) => (
                      <option key={idx} value={slot}>
                        {slot}
                      </option>
                    ))}
                  </select>
                </div>
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)}

      {selectedDept && (
        <button
          className="roster-save-btn"
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Roster"}
        </button>
      )}

      {message && <div className="message">{message}</div>}

      <SessionHandler />
    </div>
  );
};

export default RosterPage;
